import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _, { startCase } from 'lodash';
import { useNavigate } from '@wfp-common/hooks/useNavigate';
import State from '@wfp-root-app/store/state';

import { hasFeatureAccess } from '../../login/auth';
import { ManagerStatus } from '../../managers/manager';
import { HooksActionCreators } from '../partnersActions';
import { ChangeSortOptions, SORT_OPTIONS } from '../../utils/hooks/useSort';
import { defaultPartnerUserSortOptions } from '../partners';
import { Filter, MultiSelectFilter } from '../../utils/FilterTypes';
import { UserStatuses } from '../../utils/users';
import { ManagerPermission } from '../../permission-profiles/permission';

export function usePartnerUsersList(partnerId: string, pathname: string) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loadPartnerUsers = useMemo(() => HooksActionCreators.loadPartnerUsers(dispatch), []);
    const authorizePartnerUser = useMemo(() => HooksActionCreators.authorizePartnerUser(dispatch), []);
    const resetPartnerUserPassword = useMemo(() => HooksActionCreators.resetPartnerUserPassword(dispatch), []);
    const resetPartnerUserOtp = useMemo(() => HooksActionCreators.resetPartnerUserOtp(dispatch), []);
    const cancelPartnerUser = useMemo(() => HooksActionCreators.cancelPartnerUser(dispatch), []);
    const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
    const [sortingOrder, setSortingOrder] = useState<SORT_OPTIONS>(defaultPartnerUserSortOptions);
    const { partnerUsersList, auth, partner, partnerUserStatuses } = useSelector((state: State) => ({
        partnerUsersList: state.partnerUsers.list,
        auth: state.auth,
        partner: state.partners.list.find((p) => p.id.toString() === partnerId.toString()),
        partnerUserStatuses: state.appConfig.userStatuses.partnerUserStatus,
    }));
    const extendedPartnerUsersList = useMemo(
        () =>
            partnerUsersList.map((partnerUser) => ({ ...partnerUser, ...getAvailableActions({ partnerUser, auth }) })),
        [partnerUsersList, auth]
    );

    useEffect(() => {
        const statusesOptions = Object.values(partnerUserStatuses).map((status: UserStatuses) => ({
            label: startCase(status),
            value: status,
        }));

        setActiveFilters([
            new MultiSelectFilter(
                'Status',
                'status',
                [
                    { label: startCase(UserStatuses.active), value: UserStatuses.active },
                    { label: startCase(UserStatuses.parked), value: UserStatuses.parked },
                ],
                statusesOptions,
                null,
                true
            ),
        ]);
    }, []);

    useEffect(() => {
        loadPartnerUsers(
            partnerId,
            sortingOrder,
            activeFilters.filter((input) => input.isSelected)
        );
    }, [partnerId, sortingOrder, activeFilters]);

    const handleResetPassword = useCallback(
        (partnerUser) => () => {
            resetPartnerUserPassword(partnerUser);
        },
        []
    );
    const handleResetOtp = useCallback(
        (partnerUser) => () => {
            resetPartnerUserOtp(partnerUser);
        },
        []
    );
    const handlePost = useCallback(
        (partnerUser) => () => {
            authorizePartnerUser(partnerUser).catch((v) => v);
        },
        []
    );
    const handleCancel = useCallback(
        (partnerUser) => () => {
            cancelPartnerUser(partnerUser).catch((v) => v);
        },
        []
    );
    const redirectToPartnerUserPage = useCallback(
        (partnerUserId) => () => {
            navigate(`${pathname}/${partnerUserId}`);
        },
        []
    );
    const handleSort: ChangeSortOptions = ([column, direction]) => {
        if (column) {
            setSortingOrder({
                'orderBy:column': columnToAttributeMapping[column],
                'orderBy:direction': direction,
            });
        } else {
            setSortingOrder(defaultPartnerUserSortOptions);
        }
    };
    const handleFilterChanged = (filter: Filter) => {
        const activeFiltersCopy = _.clone(activeFilters);
        const index = activeFiltersCopy.findIndex((x) => x.name === filter.name);

        if (index === -1) {
            setActiveFilters(activeFiltersCopy.concat([filter]));
        } else {
            activeFiltersCopy.splice(index, 1, filter);
            setActiveFilters(activeFiltersCopy);
        }
    };
    return {
        partnerUsersList: extendedPartnerUsersList,
        manager: auth.manager,
        handleResetPassword,
        handleResetOtp,
        handlePost,
        redirectToPartnerUserPage,
        handleCancel,
        partner,
        handleSort,
        filters: activeFilters,
        handleFilterChanged,
    };
}

const columnToAttributeMapping = {
    'First Name': 'firstName',
    'Last Name': 'lastName',
    Email: 'email',
    Status: 'status',
    'Valid Until': 'expirationDate',
};

export function trClassName(partnerUser) {
    return (
        'cursor-pointer ' + (partnerUser.status === ManagerStatus.blocked ? 'failed' : '')
        //TODO add pending request indicator when those be ready
    );
}

function getAvailableActions({ partnerUser, auth }) {
    const parkedByCurrentManager = auth.manager
        ? Number(partnerUser.createdByManagerId) === Number(auth.manager.id)
        : true;
    const hasPostPermissions = hasFeatureAccess(auth, ManagerPermission.partnerUserPost);
    const hasResetCredentialsPermissions = hasFeatureAccess(auth, ManagerPermission.partnerUserPasswordReset);
    const renderCancel = partnerUser.status === 'parked' && (hasPostPermissions || parkedByCurrentManager);
    const renderPost =
        !parkedByCurrentManager && hasPostPermissions && partnerUser.status === 'parked' && !partnerUser.authorizedAt;
    const canResetActiveUser = partnerUser.status === 'active' && hasResetCredentialsPermissions;
    const renderResetPassword = canResetActiveUser;
    const renderResetOtp = canResetActiveUser;
    return { renderCancel, renderPost, renderResetPassword, renderResetOtp };
}
